import { Component, inject, signal } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ProductsSyncRequest } from '@app/services/settings/settings.service';
import moment from 'moment';

enum SyncProductsMode {
  FromDate = 'fromDate',
  SpecificProducts = 'specificProducts',
}

const PRODUCT_IDS_PATTERN = /^[\d\s,]*\d[\d\s,]*$/;

function notBefore(earliestDate: Date): ValidatorFn {
  return (control: AbstractControl<Date | null>): ValidationErrors | null => {
    if (!control.value || !moment(control.value).isBefore(earliestDate)) {
      return null;
    }
    return { beforeEarliestDate: true };
  };
}

@Component({
  selector: 'pdq-sync-products-dialog',
  standalone: false,
  templateUrl: './sync-products-dialog.component.html',
  styleUrls: ['./sync-products-dialog.component.scss'],
})
export class SyncProductsDialogComponent {
  readonly productIdsControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.pattern(PRODUCT_IDS_PATTERN)],
  });
  readonly selectedMode = signal<string>(SyncProductsMode.FromDate);
  readonly earliestSyncFromDate = moment().subtract(3, 'years').startOf('day').toDate();
  readonly syncFromDateControl = new FormControl<Date | null>(moment().subtract(2, 'weeks').startOf('day').toDate(), [
    Validators.required,
    notBefore(this.earliestSyncFromDate),
  ]);
  readonly earliestSyncFromDateIso = this.earliestSyncFromDate.toISOString();
  readonly SyncProductsMode = SyncProductsMode;
  readonly today = new Date().toISOString();

  private dialogRef = inject(MatDialogRef<SyncProductsDialogComponent, ProductsSyncRequest>);

  onConfirm(): void {
    if (this.selectedMode() === SyncProductsMode.SpecificProducts) {
      const productIds = this.productIdsControl.value.split(/[\s,]+/).filter(Boolean);
      this.dialogRef.close({ productIds });
      return;
    }
    this.dialogRef.close({ syncFromDate: this.syncFromDateControl.value });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
